import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useAuth } from '@/contexts/AuthContext';
import { AdminNavigation } from '@/components/AdminNavigation';
import {
  Plus, Edit2, Trash2, Copy, Send, Archive, Eye, Calendar,
  Package, FileText, Search, Clock, XCircle
} from 'lucide-react';
import type { Flyer, FlyerItem, Product } from '@shared/schema';

type FlyerWithItems = Flyer & {
  itemCount?: number;
  items?: (FlyerItem & { product: Product })[];
};

type StatusFilter = 'all' | 'draft' | 'scheduled' | 'published' | 'archived';

export default function AdminFlyers() {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingFlyer, setEditingFlyer] = useState<FlyerWithItems | null>(null);
  const [flyerToDelete, setFlyerToDelete] = useState<Flyer | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewFlyer, setPreviewFlyer] = useState<FlyerWithItems | null>(null);
  const [schedulingFlyer, setSchedulingFlyer] = useState<Flyer | null>(null);
  const [scheduleDate, setScheduleDate] = useState('');

  // Set page title
  useEffect(() => {
    document.title = 'Flyer Management - Tops100';
  }, []);

  // Fetch flyers
  const { data: flyers = [], isLoading, error } = useQuery<FlyerWithItems[]>({
    queryKey: ['admin-flyers', statusFilter],
    queryFn: () => apiRequest<FlyerWithItems[]>(
      statusFilter === 'all' ? '/api/admin/flyers' : `/api/admin/flyers?status=${statusFilter}`
    ),
    staleTime: 30000, // Cache for 30 seconds
  });

  // Mutations
  const createFlyerMutation = useMutation({
    mutationFn: (data: any) => apiRequest<Flyer>('POST', '/api/admin/flyers', data),
    onSuccess: (newFlyer) => {
      queryClient.invalidateQueries({ queryKey: ['admin-flyers'] });
      setIsEditorOpen(false);
      setEditingFlyer(null);
      // Navigate to visual editor
      navigate(`/admin/flyers/${newFlyer.id}/edit`);
    },
    onError: (error: any) => {
      toast({ title: 'Failed to create flyer', description: error.message, variant: 'destructive' });
    }
  });

  const updateFlyerMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      apiRequest('PUT', `/api/admin/flyers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-flyers'] });
      toast({ title: 'Flyer updated successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to update flyer', description: error.message, variant: 'destructive' });
    }
  });

  const deleteFlyerMutation = useMutation({
    mutationFn: (id: number) => apiRequest('DELETE', `/api/admin/flyers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-flyers'] });
      toast({ title: 'Flyer deleted successfully' });
      setFlyerToDelete(null);
    },
    onError: (error: any) => {
      toast({ title: 'Failed to delete flyer', description: error.message, variant: 'destructive' });
    }
  });

  const publishFlyerMutation = useMutation({
    mutationFn: (id: number) => apiRequest('PATCH', `/api/admin/flyers/${id}/publish`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-flyers'] });
      toast({ title: 'Flyer published successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to publish flyer', description: error.message, variant: 'destructive' });
    }
  });

  const archiveFlyerMutation = useMutation({
    mutationFn: (id: number) => apiRequest('PATCH', `/api/admin/flyers/${id}/archive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-flyers'] });
      toast({ title: 'Flyer archived successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to archive flyer', description: error.message, variant: 'destructive' });
    }
  });

  const duplicateFlyerMutation = useMutation({
    mutationFn: (id: number) => apiRequest('POST', `/api/admin/flyers/${id}/duplicate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-flyers'] });
      toast({ title: 'Flyer duplicated successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to duplicate flyer', description: error.message, variant: 'destructive' });
    }
  });

  const scheduleFlyerMutation = useMutation({
    mutationFn: ({ id, scheduledPublishDate }: { id: number; scheduledPublishDate: string }) =>
      apiRequest('PATCH', `/api/admin/flyers/${id}/schedule`, { scheduledPublishDate }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-flyers'] });
      toast({ title: 'Flyer scheduled successfully' });
      setSchedulingFlyer(null);
    },
    onError: (error: any) => {
      toast({ title: 'Failed to schedule flyer', description: error.message, variant: 'destructive' });
    }
  });

  const unscheduleFlyerMutation = useMutation({
    mutationFn: (id: number) => apiRequest('PATCH', `/api/admin/flyers/${id}/unschedule`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-flyers'] });
      toast({ title: 'Flyer unscheduled (returned to draft)' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to unschedule flyer', description: error.message, variant: 'destructive' });
    }
  });

  // Filter flyers
  const filteredFlyers = useMemo(() => {
    return flyers.filter(flyer => {
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        if (!flyer.title.toLowerCase().includes(search)) return false;
      }
      return true;
    });
  }, [flyers, searchQuery]);

  // Stats
  const stats = useMemo(() => ({
    total: flyers.length,
    draft: flyers.filter(f => f.status === 'draft').length,
    scheduled: flyers.filter(f => f.status === 'scheduled').length,
    published: flyers.filter(f => f.status === 'published').length,
    archived: flyers.filter(f => f.status === 'archived').length
  }), [flyers]);

  const getStatusBadge = (status: string, scheduledDate?: Date | null) => {
    switch (status) {
      case 'draft':
        return <Badge className="bg-yellow-100 text-yellow-800">Draft</Badge>;
      case 'scheduled':
        return (
          <Badge className="bg-blue-100 text-blue-800">
            Scheduled {scheduledDate ? `for ${new Date(scheduledDate).toLocaleDateString()}` : ''}
          </Badge>
        );
      case 'published':
        return <Badge className="bg-green-100 text-green-800">Published</Badge>;
      case 'archived':
        return <Badge className="bg-gray-100 text-gray-800">Archived</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleEditFlyer = (flyer: Flyer) => {
    // Navigate to the drag-and-drop visual editor
    navigate(`/admin/flyers/${flyer.id}/edit`);
  };

  const handlePreviewFlyer = async (flyer: Flyer) => {
    try {
      const fullFlyer = await apiRequest<FlyerWithItems>(`/api/admin/flyers/${flyer.id}`);
      setPreviewFlyer(fullFlyer);
      setIsPreviewOpen(true);
    } catch (error) {
      toast({ title: 'Failed to load flyer preview', variant: 'destructive' });
    }
  };

  const openNewFlyerEditor = () => {
    setEditingFlyer(null);
    setIsEditorOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img src="/images/TopsLogoCrop.png" alt="TOPS" className="h-10 w-auto" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Weekly Flyer Manager</h1>
                <p className="text-sm text-gray-500">Create and publish weekly flyers</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">{user?.email}</span>
              <Button variant="outline" onClick={logout}>Logout</Button>
            </div>
          </div>
        </div>
      </header>

      <AdminNavigation />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <motion.div whileHover={{ scale: 1.02 }}>
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Total Flyers</p>
                    <p className="text-2xl font-bold">{stats.total}</p>
                  </div>
                  <FileText className="h-8 w-8 text-blue-200" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div whileHover={{ scale: 1.02 }}>
            <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-yellow-100 text-sm">Drafts</p>
                    <p className="text-2xl font-bold">{stats.draft}</p>
                  </div>
                  <Edit2 className="h-8 w-8 text-yellow-200" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div whileHover={{ scale: 1.02 }}>
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm">Published</p>
                    <p className="text-2xl font-bold">{stats.published}</p>
                  </div>
                  <Send className="h-8 w-8 text-green-200" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div whileHover={{ scale: 1.02 }}>
            <Card className="bg-gradient-to-br from-gray-500 to-gray-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-100 text-sm">Archived</p>
                    <p className="text-2xl font-bold">{stats.archived}</p>
                  </div>
                  <Archive className="h-8 w-8 text-gray-200" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Controls */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="flex gap-4 items-center flex-wrap">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search flyers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>

                {/* Status Filter */}
                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="draft">Drafts</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={openNewFlyerEditor} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="h-4 w-4 mr-2" />
                New Flyer
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Flyer List */}
        {error ? (
          <Card className="p-6 text-center text-red-600">
            Failed to load flyers. Please try again.
          </Card>
        ) : filteredFlyers.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No flyers found</h3>
            <p className="text-gray-500 mb-4">Create your first weekly flyer to get started.</p>
            <Button onClick={openNewFlyerEditor}>
              <Plus className="h-4 w-4 mr-2" />
              Create Flyer
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {filteredFlyers.map((flyer) => (
                <motion.div
                  key={flyer.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  whileHover={{ y: -4 }}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">{flyer.title}</CardTitle>
                          <p className="text-sm text-gray-500 mt-1">
                            <Calendar className="inline h-4 w-4 mr-1" />
                            {formatDate(flyer.weekStartDate)} - {formatDate(flyer.weekEndDate)}
                          </p>
                        </div>
                        {getStatusBadge(flyer.status, flyer.scheduledPublishDate)}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                        <span className="flex items-center gap-1">
                          <Package className="h-4 w-4" />
                          {flyer.itemCount || 0} items
                        </span>
                        <span>v{flyer.version}</span>
                      </div>

                      {flyer.notes && (
                        <p className="text-sm text-gray-500 mb-4 line-clamp-2">{flyer.notes}</p>
                      )}

                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePreviewFlyer(flyer)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Preview
                        </Button>

                        {flyer.status === 'draft' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditFlyer(flyer)}
                            >
                              <Edit2 className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700"
                              onClick={() => {
                                setSchedulingFlyer(flyer);
                                setScheduleDate('');
                              }}
                            >
                              <Clock className="h-4 w-4 mr-1" />
                              Schedule
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => publishFlyerMutation.mutate(flyer.id)}
                              disabled={publishFlyerMutation.isPending}
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Publish Now
                            </Button>
                          </>
                        )}

                        {flyer.status === 'scheduled' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditFlyer(flyer)}
                            >
                              <Edit2 className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => unscheduleFlyerMutation.mutate(flyer.id)}
                              disabled={unscheduleFlyerMutation.isPending}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Unschedule
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => publishFlyerMutation.mutate(flyer.id)}
                              disabled={publishFlyerMutation.isPending}
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Publish Now
                            </Button>
                          </>
                        )}

                        {flyer.status === 'published' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => archiveFlyerMutation.mutate(flyer.id)}
                            disabled={archiveFlyerMutation.isPending}
                          >
                            <Archive className="h-4 w-4 mr-1" />
                            Archive
                          </Button>
                        )}

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => duplicateFlyerMutation.mutate(flyer.id)}
                          disabled={duplicateFlyerMutation.isPending}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Duplicate
                        </Button>

                        {flyer.status !== 'published' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => setFlyerToDelete(flyer)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      {/* Flyer Creation Modal - simplified to dates only */}
      <FlyerEditorModal
        isOpen={isEditorOpen}
        onClose={() => {
          setIsEditorOpen(false);
          setEditingFlyer(null);
        }}
        onSave={(data) => createFlyerMutation.mutate(data)}
        isLoading={createFlyerMutation.isPending}
      />

      {/* Preview Modal */}
      <FlyerPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewFlyer(null);
        }}
        flyer={previewFlyer}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!flyerToDelete} onOpenChange={(open) => !open && setFlyerToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Flyer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{flyerToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => flyerToDelete && deleteFlyerMutation.mutate(flyerToDelete.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Schedule Dialog */}
      <Dialog open={!!schedulingFlyer} onOpenChange={(open) => !open && setSchedulingFlyer(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Flyer</DialogTitle>
            <DialogDescription>
              Choose when "{schedulingFlyer?.title}" should be automatically published.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="scheduleDate">Publish Date & Time</Label>
            <Input
              id="scheduleDate"
              type="datetime-local"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
              className="mt-2"
            />
            <p className="text-sm text-gray-500 mt-2">
              The flyer will be automatically published at this time. Published flyers always take precedence over scheduled ones.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedulingFlyer(null)}>
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                if (schedulingFlyer && scheduleDate) {
                  scheduleFlyerMutation.mutate({
                    id: schedulingFlyer.id,
                    scheduledPublishDate: new Date(scheduleDate).toISOString()
                  });
                }
              }}
              disabled={!scheduleDate || scheduleFlyerMutation.isPending}
            >
              <Clock className="h-4 w-4 mr-1" />
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Simplified Flyer Creation Modal - just dates
function FlyerEditorModal({
  isOpen,
  onClose,
  onSave,
  isLoading
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  isLoading: boolean;
}) {
  const [weekStartDate, setWeekStartDate] = useState('');
  const [weekEndDate, setWeekEndDate] = useState('');
  const { toast } = useToast();

  // Reset form when modal opens - default to next Monday-Sunday
  useState(() => {
    if (isOpen) {
      const today = new Date();
      const nextMonday = new Date(today);
      nextMonday.setDate(today.getDate() + ((8 - today.getDay()) % 7 || 7));
      const nextSunday = new Date(nextMonday);
      nextSunday.setDate(nextMonday.getDate() + 6);

      setWeekStartDate(nextMonday.toISOString().split('T')[0]);
      setWeekEndDate(nextSunday.toISOString().split('T')[0]);
    }
  });

  const handleSubmit = () => {
    if (!weekStartDate || !weekEndDate) {
      toast({ title: 'Please select start and end dates', variant: 'destructive' });
      return;
    }

    // Auto-generate title from start date
    const startDate = new Date(weekStartDate);
    const title = `Week of ${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

    onSave({
      title,
      weekStartDate: new Date(weekStartDate),
      weekEndDate: new Date(weekEndDate),
      notes: null
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Flyer</DialogTitle>
          <DialogDescription>
            Select the date range for this flyer. You'll add products in the visual editor.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={weekStartDate}
                onChange={(e) => setWeekStartDate(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={weekEndDate}
                onChange={(e) => setWeekEndDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Creating...' : 'Create & Edit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Preview Modal Component
function FlyerPreviewModal({
  isOpen,
  onClose,
  flyer
}: {
  isOpen: boolean;
  onClose: () => void;
  flyer: FlyerWithItems | null;
}) {
  if (!flyer) return null;

  const groupedItems = useMemo(() => {
    const groups: Record<string, (FlyerItem & { product: Product })[]> = {
      produce: [],
      meat: [],
      grocery: [],
      general: []
    };

    (flyer.items || []).forEach(item => {
      const section = item.section || 'general';
      if (groups[section]) {
        groups[section].push(item);
      } else {
        groups.general.push(item);
      }
    });

    return groups;
  }, [flyer.items]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Flyer Preview: {flyer.title}</DialogTitle>
          <DialogDescription>
            {new Date(flyer.weekStartDate).toLocaleDateString()} - {new Date(flyer.weekEndDate).toLocaleDateString()}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {Object.entries(groupedItems).map(([section, items]) => {
            if (items.length === 0) return null;
            return (
              <div key={section}>
                <h3 className="font-bold text-lg capitalize mb-3 text-gray-800 border-b pb-2">
                  {section}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {items.map((item) => (
                    <Card key={item.id} className="overflow-hidden">
                      <div className="h-24 bg-gray-100 flex items-center justify-center">
                        {item.product.imageUrl ? (
                          <img
                            src={item.product.imageUrl}
                            alt={item.product.description}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Package className="h-8 w-8 text-gray-300" />
                        )}
                      </div>
                      <CardContent className="p-3">
                        <p className="font-medium text-sm line-clamp-2">{item.product.description}</p>
                        {item.product.chineseName && (
                          <p className="text-xs text-gray-500">{item.product.chineseName}</p>
                        )}
                        <div className="mt-2 flex justify-between items-center">
                          <span className="font-bold text-emerald-600">
                            ${(item.flyerPrice || item.product.ourPrice).toFixed(2)}
                          </span>
                          <span className="text-xs text-gray-500">{item.product.size}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}

          {(!flyer.items || flyer.items.length === 0) && (
            <p className="text-center text-gray-500 py-8">No products in this flyer yet.</p>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
